import { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Password input with a built-in show/hide toggle.
 *
 * Visually behaves like a normal <input> (inherits className) but appends a
 * right-aligned eye icon that swaps between Eye / EyeOff and toggles the
 * native input type between "password" and "text".
 *
 * - All props are forwarded to the underlying <input>.
 * - The wrapper accepts `wrapperClassName` to style the outer flex container.
 * - The toggle button gets a stable `data-testid` derived from
 *   `props["data-testid"]` (e.g. "login-password-input-toggle") so existing
 *   tests for the field id keep working.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = "", wrapperClassName = "", ...props },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const baseTestId = props["data-testid"];
  const toggleTestId = baseTestId ? `${baseTestId}-toggle` : "password-toggle";

  return (
    <div className={`relative ${wrapperClassName}`}>
      <input
        ref={ref}
        {...props}
        type={visible ? "text" : "password"}
        className={`pr-10 ${className}`.trim()}
      />
      <button
        type="button"
        data-testid={toggleTestId}
        aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        onClick={() => setVisible((v) => !v)}
        // Negative tabindex so the toggle doesn't intercept tab navigation
        // between form fields — the user reaches it deliberately if needed.
        tabIndex={-1}
        className="absolute inset-y-0 right-0 px-3 flex items-center text-[var(--vidoc-text-tertiary)] hover:text-[var(--vidoc-text)]"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});

export default PasswordInput;
